import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import createSagaMiddleware from "redux-saga";
import rootSaga from "./saga/index";
import { homeSliceReducer } from "./slices/home";
import { productListSliceReducer } from "./slices/product-list";
import { productDetailScileReducer } from "./slices/product-detail";
import { myAccountSliceReducer } from "./slices/my-account";
import { authReducer } from "./slices/auth";
import { cartSliceReducer } from "./slices/cart";
import { faqSliceReducer } from "./slices/faq";
import { partnerDetailsSliceReducer } from "./slices/partner-details";
import { externalContractSliceReducer } from "./slices/external-contract";
import { userSliceReducer } from "./slices/user";
import { notificationSliceReducer } from "./slices/notification";
import { reportsSliceReducer } from "./slices/reports";
import { engagementFormSliceReducer } from "./slices/engagement-form";
import { onboardingFormSliceReducer } from "./slices/onboarding-form";
import { notesSliceReducer } from "./slices/notes";
import { manageApplicationSliceReducer } from "./slices/manage-application";
import { globalLogsSliceReducer } from "./slices/global-logs";
import { partnerLogsSliceReducer } from "./slices/partner-logs";
import { partnerTierSliceReducer } from "./slices/partner-program";
import { partnerLicenceSliceReducer } from "./slices/partner-program/licence-management";
import { partnerRegionalMultiplierSliceReducer } from "./slices/partner-program/regional-multipliers";
import { partnerEngagementConfigurationSliceReducer } from "./slices/partner-program/engagement-configuration";
import { projectsSliceReducer } from "./slices/projects";
import { projectValidationSliceReducer } from "./slices/project-validation";
import { partnerProgramEventConfigurationSliceReducer } from "./slices/partner-program/calculator";
import { chatbotSliceReducer } from "./slices/chatbot";
import { validationDocumentationSliceReducer } from "./slices/validation-documentation";
import { validationStatusSliceReducer } from "./slices/validation-status";
import { lookupDataSliceReducer } from "./slices/lookup-data";
import { partnerProgramBenefitsSliceReducer } from "./slices/partner-program/benefits";
import { benefitsLogSliceReducer } from "./slices/partner-program/benefits-log";
import { benefitActivityDashboardSliceReducer } from "./slices/partner-program/benefit-activity-dashboard";
import { featureManagementSliceReducer } from "./slices/feature-management";
import { integrationStatusSliceReducer } from "./slices/integration-status";
import { partnerDashboardSliceReducer } from "./slices/partner-dashboard";
import { adminHomeDashboardSliceReducer } from "./slices/admin-home-dashboard";
import { technologySettingsPhasesSliceReducer } from "./slices/technology-settings/phases";
import { technologySettingsTechnologiesSliceReducer } from "./slices/technology-settings/technologies";

let sagaMiddleware = createSagaMiddleware();

const rootReducer = combineReducers({
  auth: authReducer,
  home: homeSliceReducer,
  plp: productListSliceReducer,
  cart: cartSliceReducer,
  pdp: productDetailScileReducer,
  my_account: myAccountSliceReducer,
  faq: faqSliceReducer,
  partner_details: partnerDetailsSliceReducer,
  external_contract: externalContractSliceReducer,
  user: userSliceReducer,
  notification: notificationSliceReducer,
  reports: reportsSliceReducer,
  engagement_form: engagementFormSliceReducer,
  onboarding_form: onboardingFormSliceReducer,
  notes: notesSliceReducer,
  manage_application: manageApplicationSliceReducer,
  global_logs: globalLogsSliceReducer,
  partner_logs: partnerLogsSliceReducer,
  partner_program: partnerTierSliceReducer,
  partner_licenceProgram: partnerLicenceSliceReducer,
  partner_regionalmultiplier: partnerRegionalMultiplierSliceReducer,
  partner_engagementConfiguration: partnerEngagementConfigurationSliceReducer,
  projects: projectsSliceReducer,
  project_validation: projectValidationSliceReducer,
  partner_program_event_configuration: partnerProgramEventConfigurationSliceReducer,
  partner_program_benefits: partnerProgramBenefitsSliceReducer,
  partner_program_benefits_log: benefitsLogSliceReducer,
  benefit_activity_dashboard: benefitActivityDashboardSliceReducer,
  chatbot: chatbotSliceReducer,
  validation_documentation: validationDocumentationSliceReducer,
  validation_status: validationStatusSliceReducer,
  lookup_data: lookupDataSliceReducer,
  feature_management: featureManagementSliceReducer,
  integration_status: integrationStatusSliceReducer,
  partner_dashboard: partnerDashboardSliceReducer,
  admin_home_dashboard: adminHomeDashboardSliceReducer,
  technology_settings_phases: technologySettingsPhasesSliceReducer,
  technology_settings_technologies: technologySettingsTechnologiesSliceReducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware({
      thunk: false,
      serializableCheck: false,
    }).concat(sagaMiddleware);
  },
});

sagaMiddleware.run(rootSaga);
export type RootStateType = ReturnType<typeof rootReducer>;
export const useAppDispatch: () => typeof store.dispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<
  ReturnType<typeof store.getState>
> = useSelector;
